// Personal-best laps (time, sectors and the path for the ghost) stored on the
// device in IndexedDB. Nothing leaves the phone. Falls back to memory if
// IndexedDB is unavailable (e.g. some private-browsing modes).

import type { LapSample } from '../sim/lapRecorder';

export interface StoredLap {
  lapTime: number;
  sectors: Array<number | undefined>;
  samples: LapSample[];
  date: string;
}

const DB_NAME = 'fone-gp';
const STORE = 'bestLaps';
const memory = new Map<string, StoredLap>();

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no indexedDB'));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadBest(key: string): Promise<StoredLap | undefined> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result as StoredLap | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return memory.get(key);
  }
}

export async function saveBest(key: string, lap: StoredLap): Promise<void> {
  memory.set(key, lap);
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(lap, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Kept in memory for this session.
  }
}
